import {
  ACCOUNT_READ_ONLY_CODE,
  getAxiosErrorResponseData,
  getMessageFromResponse,
  humanizeApiErrorBody,
  isAxiosGenericStatusMessage,
  parseStructuredApiError,
  PLAN_FEATURE_DISABLED_CODE,
  PUBLIC_API_QUOTA_EXCEEDED_CODE,
  QUOTA_EXCEEDED_CODE,
  WORKSPACE_ACCESS_DENIED_CODE,
} from '@trove/api-client';
import type { TFunction } from 'i18next';

function featureLabel(t: TFunction, feature?: string): string {
  if (!feature) return t('apiErrors.genericFeature', { ns: 'common' });
  return t(`apiErrors.features.${feature}`, {
    ns: 'common',
    defaultValue: feature,
  });
}

function planLabel(t: TFunction, plan?: string): string {
  if (!plan) return t('apiErrors.plans.paid', { ns: 'common' });
  return t(`apiErrors.plans.${plan}`, {
    ns: 'common',
    defaultValue: plan,
  });
}

/**
 * Maps structured API errors (entitlements, quotas, roles) to localized toast copy.
 * Falls back to interceptor-humanized `Error.message`, then the provided fallback.
 */
export function formatApiErrorMessage(error: unknown, t: TFunction, fallback: string): string {
  const data = getAxiosErrorResponseData(error);
  const structured = parseStructuredApiError(data);

  if (structured) {
    const feature = featureLabel(t, structured.feature);
    const plan = planLabel(t, structured.upgradeTo);

    switch (structured.code) {
      case PLAN_FEATURE_DISABLED_CODE:
        return t('apiErrors.planFeatureDisabled', {
          ns: 'common',
          feature,
          plan,
        });
      case QUOTA_EXCEEDED_CODE:
        if (typeof structured.used === 'number' && typeof structured.max === 'number') {
          return t('apiErrors.quotaExceeded', {
            ns: 'common',
            feature,
            used: structured.used,
            max: structured.max,
          });
        }
        return t('apiErrors.quotaExceededNoCounts', {
          ns: 'common',
          feature,
        });
      case ACCOUNT_READ_ONLY_CODE:
        return t('apiErrors.accountReadOnly', { ns: 'common' });
      case PUBLIC_API_QUOTA_EXCEEDED_CODE:
        return t('apiErrors.publicApiQuotaExceeded', { ns: 'common' });
      case WORKSPACE_ACCESS_DENIED_CODE:
        return t('apiErrors.workspaceAccessDenied', { ns: 'common' });
      case 'ACCOUNT_ADMIN_REQUIRED':
        return t('apiErrors.accountAdminRequired', { ns: 'common' });
      case 'ACCOUNT_OWNER_REQUIRED':
        return t('apiErrors.accountOwnerRequired', { ns: 'common' });
      default:
        break;
    }
  }

  const human = humanizeApiErrorBody(data);
  if (human) return human;

  const fromBody = getMessageFromResponse(data);
  if (fromBody && !isAxiosGenericStatusMessage(fromBody) && !fromBody.includes('requires a higher plan')) {
    return fromBody;
  }

  if (error instanceof Error && error.message && !isAxiosGenericStatusMessage(error.message)) {
    return error.message;
  }

  return fallback;
}
